import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { conRespaldo, guardarUltimo } from '@/lib/campo/loUltimoQueSeSupo'
import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import {
  buscarEnCatalogo,
  cuandoSeDescargo,
  cuantosGuardados,
  guardarCatalogo,
  guardarClientes,
} from '@/lib/campo/catalogoLocal'

/**
 * Cotizar desde la app.
 *
 * La aplicación nunca habla con World Office: encola una solicitud y un worker
 * la convierte en cotización real, devolviendo el número. Aquí viven la
 * búsqueda del catálogo y la espera de esa respuesta.
 */

// ─── Catálogo ───────────────────────────────────────────────────────────────

export type ProductoCatalogo = {
  id_inventario: number
  descripcion: string
  codigo_barras: string | null
  precio_sin_iva: number | null
  precio_con_iva: number | null
  iva: number | null
  es_mano_de_obra: boolean
  precio_a_convenir: boolean
  /** Existencias en bodega (wo_productos.stock_total). Negativo/0 = no hay. */
  stock: number
  /** Unidad del maestro de World Office. Null mientras el worker no la mande. */
  unidad: string | null
  /** Listas 2 y 3 de World Office, con IVA; null si no tiene (0285). */
  precio2_con_iva?: number | null
  precio3_con_iva?: number | null
}

/**
 * EL PRECIO CON IVA EN LA LISTA DEL CLIENTE (0285).
 *
 * World Office tiene tres listas por producto y cada cliente una asignada
 * (Terceros.ListPrecios): la cotización de la OS-2026-0113 salió con la 1
 * ($230.000 el medidor) y la factura con la 3 del cliente ($225.000). Si la
 * lista del cliente no tiene precio para el producto, la 1, como fn_precio_de_lista.
 */
export function precioConIvaEnLista(p: ProductoCatalogo, lista: number): number | null {
  const deLista = lista === 2 ? p.precio2_con_iva : lista === 3 ? p.precio3_con_iva : null
  const n = deLista === null || deLista === undefined ? 0 : Number(deLista)
  return n > 0 ? n : p.precio_con_iva === null ? null : Number(p.precio_con_iva)
}

/** La lista de precios del cliente de la orden (o del tercero, en venta libre). */
export function useListaDePrecios(ordenServicioId: string | null, idTercero: number | null) {
  return useQuery({
    queryKey: ['lista-de-precios', ordenServicioId, idTercero],
    enabled: Boolean(ordenServicioId || idTercero),
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<number> => {
      const { data, error } = await supabase.rpc('fn_lista_de_precios', {
        p_orden_servicio_id: ordenServicioId,
        p_id_tercero: idTercero,
      })
      if (error) return 1
      return Number(data) || 1
    },
  })
}

/**
 * Busca en los 2.516 productos de World Office.
 *
 * No se traen todos: se busca con al menos dos letras y se cortan los
 * resultados. Un desplegable con dos mil quinientas líneas no se usa, se
 * padece.
 */
export function useCatalogo(termino: string) {
  const [sinSenal, setSinSenal] = useState(false)

  const consulta = useQuery({
    queryKey: ['catalogo', termino],
    enabled: termino.trim().length >= 2,
    staleTime: 5 * 60 * 1000,
    // Sin señal no sirve de nada reintentar cuatro veces: se cae enseguida al
    // catálogo del teléfono y el técnico sigue cotizando.
    retry: 0,
    /*
     * QUE LA CONSULTA CORRA AUNQUE EL NAVEGADOR SE DECLARE SIN RED.
     *
     * React Query, por defecto, ni siquiera llama a la función cuando
     * `navigator.onLine` es false: deja la consulta «en pausa». Con eso, todo
     * el camino al catálogo del teléfono quedaba muerto —se probó cortando la
     * red de verdad: el buscador devolvía cero aunque en IndexedDB estaban
     * los productos—. Aquí quien decide qué hacer sin señal es la función, no
     * la librería.
     */
    networkMode: 'offlineFirst',
    queryFn: async (): Promise<ProductoCatalogo[]> => {
      /*
       * EL CATÁLOGO DEL TELÉFONO, CUANDO NO HAY SEÑAL.
       *
       * En muchas obras no la hay, y hasta ahora cada letra del buscador iba
       * al servidor: sin internet no se podía cotizar. El catálogo entero
       * cabe en el teléfono, así que se busca allí y se sigue trabajando.
       *
       * Lo que no viaja es el stock —Johan fijó esa frontera: «no
       * necesitamos stock real, ese puede ser con señal»—, porque un número
       * descargado el martes se usaría el jueves como si fuera de hoy.
       */
      if (!navigator.onLine) {
        setSinSenal(true)
        return buscarEnCatalogo(termino.trim())
      }

      try {
        const { data, error } = await supabase
          .from('vista_catalogo')
          .select('*')
          .ilike('descripcion', `%${termino.trim()}%`)
          .order('descripcion')
          .limit(25)

        if (error) throw new Error(mensajeDeError(error))

        setSinSenal(false)
        return (data ?? []) as unknown as ProductoCatalogo[]
      } catch (fallo) {
        // «Con internet» no es lo mismo que «el servidor contesta»: en la
        // obra se queda uno con una raya de señal que no transmite. Si la
        // consulta no sale, se busca en lo guardado antes de rendirse.
        const guardados = await buscarEnCatalogo(termino.trim())
        if (guardados.length > 0) {
          setSinSenal(true)
          return guardados
        }
        throw fallo
      }
    },
  })

  return {
    productos: consulta.data ?? [],
    buscando: consulta.isFetching,
    /** Se está buscando en el catálogo del teléfono: el stock no es de fiar. */
    sinSenal,
  }
}

/**
 * MANTENER EL CATÁLOGO DESCARGADO.
 *
 * Se refresca al entrar si está vacío o si lo guardado tiene más de un día.
 * No se pide nunca sin señal, y si falla no pasa nada: queda lo de ayer, que
 * para precios de materiales es suficiente y es infinitamente mejor que nada.
 */
export function useDescargarCatalogo() {
  const [estado, setEstado] = useState<{ productos: number; cuando: string | null }>({
    productos: 0,
    cuando: null,
  })

  useEffect(() => {
    let vivo = true

    void (async () => {
      const cuando = await cuandoSeDescargo()
      const cuantos = await cuantosGuardados()
      if (vivo) setEstado({ productos: cuantos, cuando })

      /*
       * LA TARIFA DE MANO DE OBRA, CADA VEZ QUE HAY SEÑAL.
       *
       * Sin esto solo quedaba guardada si el técnico había abierto el
       * cotizador alguna vez con cobertura. Esteban no lo había hecho, y sin
       * señal la mano de obra le caía al precio del catálogo y editable: la
       * regla de «horas por tarifa» se perdía justo en la obra. Es un número
       * solo; se pide siempre, no una vez al día como el catálogo.
       */
      if (navigator.onLine) {
        try {
          const { data } = await supabase.rpc('fn_config', { p_clave: 'tarifa_mano_obra_hora' })
          const tarifa = Math.round(Number(data ?? 0)) || 0
          if (tarifa > 0) guardarUltimo('tarifa-mano-obra', tarifa)
        } catch {
          /* se queda la de la última vez */
        }
      }

      const viejo = cuando === null || Date.now() - new Date(cuando).getTime() > 24 * 60 * 60 * 1000

      if (!navigator.onLine || (!viejo && cuantos > 0)) return

      try {
        // Por páginas: el servidor corta en mil filas y el catálogo pasa de
        // dos mil seiscientos.
        const todos: ProductoCatalogo[] = []
        for (let desde = 0; ; desde += 1000) {
          const { data, error } = await supabase
            .from('vista_catalogo')
            .select('*')
            .order('id_inventario')
            .range(desde, desde + 999)

          if (error) throw new Error(mensajeDeError(error))
          const pagina = (data ?? []) as unknown as ProductoCatalogo[]
          todos.push(...pagina)
          if (pagina.length < 1000) break
        }

        await guardarCatalogo(todos)

        // Y los clientes, por lo mismo: sin ellos no se puede levantar una
        // obra en la calle.
        const terceros: Record<string, unknown>[] = []
        for (let desde = 0; ; desde += 1000) {
          const { data, error } = await supabase
            .from('vista_clientes')
            .select('*')
            .order('id_tercero')
            .range(desde, desde + 999)

          if (error) break
          const pagina = (data ?? []) as unknown as Record<string, unknown>[]
          terceros.push(...pagina)
          if (pagina.length < 1000) break
        }
        if (terceros.length > 0) await guardarClientes(terceros)
        if (vivo) setEstado({ productos: todos.length, cuando: new Date().toISOString() })
      } catch {
        /* Se queda lo de antes: un catálogo de ayer sirve, ninguno no. */
      }
    })()

    return () => {
      vivo = false
    }
  }, [])

  return estado
}

/**
 * El stock de un puñado de productos, por id.
 *
 * El buscador solo trae los del término actual; los renglones ya puestos (o los
 * de un borrador que se reabre) no pasan por él. Este hook consulta el stock de
 * esos ids directamente, para que la alerta de "no hay / faltan X" funcione en
 * cada renglón aunque no se haya buscado. Devuelve un mapa id → stock.
 */
export function useStock(ids: number[]) {
  // Orden estable en la clave: si no, dos renders con los mismos ids en distinto
  // orden dispararían dos consultas.
  const clave = [...new Set(ids)].sort((a, b) => a - b)

  const consulta = useQuery({
    queryKey: ['stock', clave],
    enabled: clave.length > 0,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<Map<number, number>> => {
      const { data, error } = await supabase
        .from('vista_catalogo')
        .select('id_inventario, stock')
        .in('id_inventario', clave)

      if (error) throw new Error(mensajeDeError(error))

      const mapa = new Map<number, number>()
      for (const fila of (data ?? []) as { id_inventario: number; stock: number | null }[]) {
        mapa.set(fila.id_inventario, Number(fila.stock ?? 0))
      }
      return mapa
    },
  })

  return consulta.data ?? new Map<number, number>()
}

/**
 * El producto de mano de obra.
 *
 * Se trae aparte porque el cotizador lo ofrece siempre: es la línea de la que
 * el tablero saca el margen, y hoy solo 224 de 2.616 cotizaciones la separan.
 */
export function useProductoManoDeObra() {
  const consulta = useQuery({
    queryKey: ['catalogo', 'mano-de-obra'],
    staleTime: 60 * 60 * 1000,
    queryFn: async (): Promise<ProductoCatalogo | null> => {
      // Con igualdad exacta no aparecía: en World Office está guardado como
      // 'MANO DE OBRA ', con un espacio al final. Se busca por coincidencia y
      // se toma el más corto, que es el genérico y no una variante como
      // 'MANO DE OBRA CORRECCIONES CERTIFICACION RETIE'.
      const { data, error } = await supabase
        .from('vista_catalogo')
        .select('*')
        .ilike('descripcion', '%MANO DE OBRA%')
        .order('descripcion')

      if (error) throw new Error(mensajeDeError(error))

      const candidatos = (data ?? []) as unknown as ProductoCatalogo[]

      return (
        candidatos.filter((p) => p.descripcion.trim().toUpperCase() === 'MANO DE OBRA').at(0) ??
        null
      )
    },
  })

  return consulta.data ?? null
}

/**
 * La tarifa de mano de obra por hora (una sola para la empresa).
 *
 * Con ella la mano de obra se calcula por las horas del trabajo: el renglón de
 * mano de obra usa esta tarifa como precio unitario y las horas como cantidad,
 * así el valor = horas × tarifa. La fija gerencia en la configuración; si está
 * en cero, el cotizador cae al precio del catálogo como antes.
 */
export function useTarifaManoObra(): number {
  const consulta = useQuery({
    queryKey: ['config', 'tarifa-mano-obra'],
    staleTime: 30 * 60 * 1000,
    queryFn: async (): Promise<number> => {
      /*
       * LA TARIFA SE RECUERDA, PORQUE SIN ELLA LA MANO DE OBRA VALE CERO.
       *
       * El técnico cotiza en la obra, donde no hay señal. Si esta consulta
       * falla, `consulta.data` es undefined y la tarifa cae a 0: la
       * cotización sale con la mano de obra en cero y eso es lo que ve el
       * cliente. Un número que no es una tarifa haciendo de tarifa.
       *
       * Es un solo número y cambia dos veces al año: se guarda.
       */
      return conRespaldo<number>('tarifa-mano-obra', async () => {
        const { data, error } = await supabase.rpc('fn_config', {
          p_clave: 'tarifa_mano_obra_hora',
        })
        if (error) throw new Error(mensajeDeError(error))
        return Math.round(Number(data ?? 0)) || 0
      })
    },
  })

  return consulta.data ?? 0
}

/**
 * Separa el código del nombre.
 *
 * Los productos llegan como '0043 - CABLE 08 AMARILLO CENTELSA'. Mostrarlo
 * entero obliga a leer un código antes de llegar al nombre, que es lo que la
 * persona está buscando.
 */
export function partirDescripcion(descripcion: string): { codigo: string | null; nombre: string } {
  const separador = descripcion.indexOf(' - ')

  if (separador > 0 && separador <= 12) {
    return {
      codigo: descripcion.slice(0, separador).trim(),
      nombre: descripcion.slice(separador + 3).trim(),
    }
  }

  return { codigo: null, nombre: descripcion.trim() }
}

// ─── La solicitud ───────────────────────────────────────────────────────────

export type LineaCotizacion = {
  id_inventario: number
  descripcion: string
  cantidad: number
  precio_unitario: number
  iva: number
  es_mano_de_obra: boolean
  /**
   * La unidad del renglón: «Und.», «m»…
   *
   * Nace de la del catálogo y el técnico puede cambiarla, porque en World
   * Office la unidad se ajusta EN EL RENGLÓN y no en el producto: de 2.680
   * productos del maestro solo 7 dicen «m», y sin embargo hay cientos de
   * renglones facturados por metro. Null = que allá apliquen la del maestro.
   */
  unidad?: string | null
}

export type EstadoSolicitud = {
  estado: 'pending' | 'done' | 'error'
  numero_devuelto: string | null
  wo_total: number | null
  error_msg: string | null
  intentos: number
  segundos: number
}

/** Pide la cotización y devuelve el id de la que quedó en la matriz. */
export function useSolicitarCotizacion() {
  const clienteConsultas = useQueryClient()

  return useMutation({
    mutationFn: async ({
      ordenServicioId,
      lineas,
      nota,
    }: {
      ordenServicioId: string
      lineas: LineaCotizacion[]
      nota?: string
    }): Promise<string> => {
      const { data, error } = await supabase.rpc('fn_solicitar_cotizacion', {
        p_orden_servicio_id: ordenServicioId,
        // Al outbox va el precio SIN IVA: es el valor unitario que World
        // Office guarda. El de con IVA es solo para mostrar.
        p_items: lineas.map((l) => ({
          id_inventario: l.id_inventario,
          cantidad: l.cantidad,
          precio_unitario: l.precio_unitario,
          iva: l.iva,
        })),
        p_nota: nota ?? null,
      })

      if (error) throw new Error(mensajeDeError(error))

      return data as string
    },
    onSuccess: async () => {
      await clienteConsultas.invalidateQueries({ queryKey: ['ordenes-servicio'] })
      await clienteConsultas.invalidateQueries({ queryKey: ['pendientes-de-decision'] })
    },
  })
}

/**
 * Sigue una solicitud hasta que World Office responde.
 *
 * El worker corre cada veinte segundos, así que se consulta cada tres. Se deja
 * de preguntar a los dos minutos: si no volvió, el problema no es la espera y
 * seguir consultando solo esconde que algo falló.
 */
export function useEsperarCotizacion(cotizacionId: string | null) {
  const [agotado, setAgotado] = useState(false)

  useEffect(() => {
    setAgotado(false)
    if (!cotizacionId) return

    const tope = setTimeout(() => setAgotado(true), 120_000)
    return () => clearTimeout(tope)
  }, [cotizacionId])

  const consulta = useQuery({
    queryKey: ['solicitud-cotizacion', cotizacionId],
    enabled: Boolean(cotizacionId) && !agotado,
    refetchInterval: (consultaActual) => {
      const dato = consultaActual.state.data as EstadoSolicitud | undefined
      return dato && dato.estado !== 'pending' ? false : 3000
    },
    queryFn: async (): Promise<EstadoSolicitud | null> => {
      const { data, error } = await supabase
        .from('vista_solicitudes_cotizacion')
        .select('estado, numero_devuelto, wo_total, error_msg, intentos, segundos')
        .eq('cotizacion_id', cotizacionId!)
        .maybeSingle()

      if (error) throw new Error(mensajeDeError(error))

      return (data ?? null) as unknown as EstadoSolicitud | null
    },
  })

  return {
    solicitud: consulta.data ?? null,
    agotado,
  }
}
